import type { ReactNode } from 'react';
import {
  Bookmark,
  Captions,
  CaptionsOff,
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { formatClock } from '@mlm/shared';
import { cn } from '../lib/cn';
import { Slider } from '../components/slider';
import { Scrubber } from './Scrubber';
import { SpeedMenu } from './SpeedMenu';
import type { PlaybackAdapter, PlayerSnapshot } from './adapter';

/**
 * Icon control. `onMouseDown` preventDefault keeps focus off the bar so the
 * keyboard shortcuts keep reaching the player, and both `aria-label` and
 * `title` are set — `title` is this repo's tooltip (there is no Tooltip
 * primitive in @mlm/ui).
 */
function IconButton({
  label,
  onClick,
  children,
  pressed,
  className,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 flex-none items-center justify-center rounded text-white/90 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * App-owned chrome for R2 (`<video>`) lessons.
 *
 * Embeds keep their provider's own controls — rebuilding YouTube's quality menu
 * would lose real functionality. The asymmetry worth closing runs the other
 * way: a bare `<video>` gets browser-default controls, and iOS hides the
 * playback-rate control entirely, so a self-hosted lesson felt like the poor
 * relation next to a Vimeo one in the same course.
 */
export function ControlBar({
  snapshot,
  adapter,
  visible,
  fullscreen,
  onToggleFullscreen,
  onBookmark,
  onSeekPreview,
  onSeekCommit,
  onRateChange,
  onToggleCaptions,
  seekLimit,
  onSeekBlocked,
}: {
  snapshot: PlayerSnapshot;
  adapter: PlaybackAdapter;
  visible: boolean;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  onBookmark?: () => void;
  onSeekPreview: (seconds: number) => void;
  onSeekCommit: (seconds: number) => void;
  onRateChange: (rate: number) => void;
  onToggleCaptions?: () => void;
  /** Furthest seekable second; null = unrestricted. */
  seekLimit?: number | null;
  onSeekBlocked?: () => void;
}) {
  const playing = snapshot.state === 'playing';
  const duration = snapshot.duration ?? 0;
  const captionsOn = snapshot.captionsLang != null;

  return (
    <div
      // `transition-opacity`, not `animate-in`: tailwindcss-animate is not
      // installed in this repo, so those utility classes are dead no-ops.
      className={cn(
        'absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-1.5 pt-6 transition-opacity',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <Scrubber
        value={snapshot.currentTime}
        max={duration}
        buffered={snapshot.buffered}
        onPreview={onSeekPreview}
        onCommit={onSeekCommit}
        seekLimit={seekLimit}
        onSeekBlocked={onSeekBlocked}
      />
      <div className="flex items-center gap-1">
        <IconButton
          label={playing ? 'Pause' : 'Play'}
          onClick={() => (playing ? adapter.pause() : void adapter.play())}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </IconButton>
        <IconButton
          label="Back 10 seconds"
          onClick={() => adapter.seek(Math.max(0, snapshot.currentTime - 10))}
        >
          <RotateCcw className="h-4 w-4" />
        </IconButton>
        <IconButton
          label="Forward 10 seconds"
          onClick={() => {
            // Same clamp the scrubber applies, so +10s cannot step past the
            // watched boundary one press at a time.
            const target = snapshot.currentTime + 10;
            if (seekLimit != null && target > seekLimit) {
              onSeekBlocked?.();
              adapter.seek(seekLimit);
              return;
            }
            adapter.seek(target);
          }}
        >
          <RotateCw className="h-4 w-4" />
        </IconButton>
        <IconButton
          label={snapshot.muted ? 'Unmute' : 'Mute'}
          onClick={() => adapter.setMuted(!snapshot.muted)}
        >
          {snapshot.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </IconButton>
        {/* Touch devices have hardware volume; the slider only costs space there. */}
        <div className="hidden w-20 sm:block">
          <Slider
            value={[snapshot.muted ? 0 : Math.round(snapshot.volume * 100)]}
            max={100}
            step={1}
            aria-label="Volume"
            onValueChange={([next]) => {
              if (next == null) return;
              adapter.setMuted(next === 0);
              adapter.setVolume(next / 100);
            }}
          />
        </div>
        <span className="ml-1 select-none text-[11px] tabular-nums text-white/80">
          {formatClock(snapshot.currentTime)} / {formatClock(duration)}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {snapshot.rateSupported && (
            <SpeedMenu rate={snapshot.rate} rates={snapshot.rates} onChange={onRateChange} />
          )}
          {snapshot.captionsAvailable && onToggleCaptions && (
            <IconButton
              label={captionsOn ? 'Turn captions off' : 'Turn captions on'}
              pressed={captionsOn}
              onClick={onToggleCaptions}
            >
              {captionsOn ? <Captions className="h-4 w-4" /> : <CaptionsOff className="h-4 w-4" />}
            </IconButton>
          )}
          {onBookmark && (
            <IconButton label="Bookmark this moment" onClick={onBookmark}>
              <Bookmark className="h-4 w-4" />
            </IconButton>
          )}
          <IconButton
            label={fullscreen ? 'Exit full screen' : 'Full screen'}
            onClick={onToggleFullscreen}
          >
            {fullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </IconButton>
        </div>
      </div>
    </div>
  );
}
